/* eslint-disable jsx-a11y/label-has-associated-control */
import { useState, useRef } from 'react';
import ErrorMessages from '../../utils/errorMessage';
import { highlight, removeHighlight } from '../../utils/helpers';
import { SecurityTypes } from './declaration';
import Footer from './footer';

interface Props {
  securityData: SecurityTypes;
  step: number;
  setSecurityData: (obj: SecurityTypes) => void;
  prevStep: () => void;
  submit: () => void;
}

function Security({
  securityData,
  setSecurityData,
  prevStep,
  submit,
  step,
}: Props) {
  const [isFieldsDisable, setIsFieldsDisable] = useState<boolean>(true);
  const [validationMsg, setValidationMsg] = useState<string>('');
  const [validKeyName, setValidKeyName] = useState(true);
  const [validKey, setValidKey] = useState(true);

  const jwtNameRef = useRef<HTMLInputElement>(null);
  const jwtKeyRef = useRef<HTMLTextAreaElement>(null);

  const allowNext = () => {
    if (!securityData || !securityData.requireJWT) {
      return false;
    }
    if (securityData) {
      if (securityData.requireJWT) {
        if (!validKeyName) {
          return true;
        }
        if (!validKey) {
          return true;
        }
        return false;
      }
    }
    return true;
  };

  const handleChange = (name: string, value: string) => {
    switch (name) {
      case 'JWTKeyName':
        if (value.length < 3) {
          highlight(jwtNameRef, 'border');
          setValidationMsg(ErrorMessages.minLengthError('JWT public key name', 3));
          setValidKeyName(false);
          return;
        }
        if (value.length > 20) {
          highlight(jwtNameRef, 'border');
          setValidationMsg(ErrorMessages.maxLengthError('JWT public key name', 20));
          setValidKeyName(false);
          return;
        }
        removeHighlight(jwtNameRef, 'border');
        setValidationMsg('');
        setValidKeyName(true);

        setSecurityData({ ...securityData, JWTKeyName: value });
        break;
      case 'JWTKey':
        if (value.length < 170) {
          highlight(jwtKeyRef, 'border');
          setValidationMsg(ErrorMessages.minLengthError('JWT public key', 170));
          setValidKey(false);
          return;
        }
        if (value.length > 800) {
          highlight(jwtKeyRef, 'border');
          setValidationMsg(ErrorMessages.maxLengthError('JWT public key', 800));
          setValidKey(false);
          return;
        }
        removeHighlight(jwtKeyRef, 'border');
        setValidationMsg('');
        setValidKey(true);

        setSecurityData({ ...securityData, JWTKey: value });
        break;
      default:
        break;
    }
  };

  const checkboxHandler = () => {
    if (securityData.requireJWT) {
      setIsFieldsDisable(true);
      removeHighlight(jwtNameRef, 'border');
      removeHighlight(jwtKeyRef, 'border');
      setSecurityData({ ...securityData, requireJWT: false });
    } else {
      highlight(jwtNameRef, 'border');
      highlight(jwtKeyRef, 'border');
      setIsFieldsDisable(false);
      setValidKey(false);
      setValidKeyName(false);
      setSecurityData({ ...securityData, requireJWT: true });
    }
  };

  return (
    <>
      <div className="my-4 col-xl-10 col-lg-12 col-sm-11 col-md-11 col-11 mx-auto border-bottom">
        <h5 className="mt-4 mb-5">JWT</h5>
        <div className="form-check mb-3 ms-3">
          <input
            className="form-check-input"
            type="checkbox"
            checked={securityData ? securityData.requireJWT : false}
            onChange={checkboxHandler}
          />
          <label className="form-check-label ms-2" htmlFor="flexCheckDefault">
            Require JWT
          </label>
          <i
            title="Restrict API usage to requests that include a valid JWT"
            className="fa-solid fa-circle-info ms-1"
          />
        </div>
        <div className="row align-items-center ms-3">
          <label
            htmlFor=""
            className="col-xl-2 col-lg-6 col-md-4 col-sm-12 col-form-label ps-0"
          >
            JWT Public Key Name
            <span className="text-danger">*</span>
          </label>
          <div className="col-sm-12 col-md-7 col-xl-8 col-lg-6 col-11">
            <input
              type="text"
              className="form-control"
              id="JWTKeyName"
              ref={jwtNameRef}
              value={securityData.JWTKeyName}
              onChange={(e: { target: { value: string } }) => handleChange('JWTKeyName', e.target.value)}
              disabled={isFieldsDisable}
              autoComplete="off"

            />
          </div>
        </div>
        <div className="my-5 row align-items-center ms-3">
          <label
            htmlFor=""
            className="col-xl-2 col-lg-6 col-md-4 col-sm-12 col-form-label ps-0 "
          >
            JWT Public Key
            <span className="text-danger">*</span>
            <i
              title="Restrict API usage to requests that include a valid JWT. Public key can be generated using RSA and ECDSA algorithms only."
              className="fa-solid fa-circle-info ms-1"
            />
          </label>
          <div className="col-sm-12 col-md-7 col-xl-8 col-lg-6 col-11">
            <textarea
              rows={8}
              cols={50}
              className="form-control"
              id="JWTKey"
              ref={jwtKeyRef}
              placeholder={
                isFieldsDisable === false
                  ? '-----BEGIN PUBLIC KEY----------END PUBLIC KEY-----'
                  : ''
              }
              value={securityData.JWTKey}
              onChange={(e: { target: { value: string } }) => handleChange('JWTKey', e.target.value)}
              disabled={isFieldsDisable}
              autoComplete="off"

            />
          </div>
        </div>
        {validationMsg && (
          <div className="text-center mb-3" style={{ color: 'red' }}>
            {validationMsg}
          </div>
        )}
      </div>
      <Footer
        step={step}
        prevStep={prevStep}
        submit={submit}
        disable={allowNext()}
      />
    </>
  );
}

export default Security;
